import { CheckCircle2, XCircle } from "lucide-react";
import { StatusBadge, formatDate } from "./ui";
import { centsToDisplay, BILLING_TYPE_LABELS } from "@/lib/money";

const GroupedItems = ({ title, items }) => {
  if (items.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-400">{title}</p>
      <div className="overflow-hidden rounded-xl border border-slate-200">
        {items.map((item, idx) => (
          <div key={item.item_id || idx} className={`flex items-center justify-between gap-4 px-4 py-3 text-sm ${idx > 0 ? "border-t border-slate-100" : ""}`} data-testid={`quote-doc-item-${item.item_id || idx}`}>
            <div>
              <p className="font-semibold text-slate-950">{item.description}</p>
              <p className="text-xs text-slate-500">Qty {item.quantity} · {BILLING_TYPE_LABELS[item.billing_type]}</p>
            </div>
            <p className="font-bold text-slate-950">{centsToDisplay(item.unit_price_cents * item.quantity)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function QuoteDocument({ quote, mode = "admin", onAccept, onDecline, busy }) {
  const t = quote.totals || {};
  const recurringLabel = quote.billing_frequency === "Annual" ? "/ year" : "/ month";
  const oneTimeItems = (quote.items || []).filter((i) => i.billing_type === "one_time");
  const recurringItems = (quote.items || []).filter((i) => i.billing_type !== "one_time");
  const discount = quote.discount || {};
  const hasDiscount = discount.type !== "none" && (t.recurring_discount_cents > 0 || t.one_time_discount_cents > 0);
  const isCustomer = mode === "customer";
  const actionable = quote.status === "Sent" || quote.status === "Viewed";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="quote-document">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 p-6 sm:p-8">
        <img src="/nova-logo-wide.png" alt="Nova Opsis" className="h-9 w-auto object-contain" data-testid="quote-doc-logo" />
        <div className="text-right">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">Quote</p>
          <p className="text-lg font-extrabold text-slate-950" data-testid="quote-doc-id">{quote.quote_id}</p>
          <div className="mt-1 flex justify-end"><StatusBadge status={quote.status} testId="quote-doc-status" /></div>
        </div>
      </div>

      <div className="grid gap-6 border-b border-slate-100 p-6 sm:grid-cols-3 sm:p-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Prepared For</p>
          <p className="mt-1 text-sm font-bold text-slate-950" data-testid="quote-doc-company">{quote.company}</p>
          <p className="text-sm text-slate-500">{quote.contact_name}{quote.contact_email ? ` · ${quote.contact_email}` : ""}</p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Created</p>
          <p className="mt-1 text-sm font-semibold text-slate-800">{formatDate(quote.created_at)}</p>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Valid Until</p>
          <p className="mt-1 text-sm font-semibold text-slate-800" data-testid="quote-doc-valid-until">{formatDate(quote.valid_until)}</p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Billing Frequency</p>
          <p className="mt-1 text-sm font-semibold text-slate-800" data-testid="quote-doc-billing-frequency">{quote.billing_frequency}</p>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Payment Terms</p>
          <p className="mt-1 text-sm font-semibold text-slate-800">{quote.payment_terms}</p>
        </div>
      </div>

      <div className="p-6 sm:p-8">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.1em] text-slate-400">What's Included</p>
        <GroupedItems title="Recurring Charges" items={recurringItems} />
        <GroupedItems title="One-time Charges" items={oneTimeItems} />

        <div className="mt-6 space-y-2 rounded-xl bg-slate-50 p-5">
          {hasDiscount && (
            <div className="flex justify-between text-sm text-slate-600">
              <span>Discount ({discount.type === "percentage" ? `${discount.percent}%` : centsToDisplay(discount.amount_cents)})</span>
              <span>-{centsToDisplay(t.recurring_discount_cents + t.one_time_discount_cents)}</span>
            </div>
          )}
          {(quote.tax || {}).enabled && (
            <div className="flex justify-between text-sm text-slate-600">
              <span>Tax ({quote.tax.rate_percent}%)</span>
              <span>{centsToDisplay(t.recurring_tax_cents + t.one_time_tax_cents)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-extrabold text-slate-950">
            <span>Initial Total</span>
            <span data-testid="quote-doc-initial-total">{centsToDisplay(t.initial_total_cents)}</span>
          </div>
          {t.recurring_total_cents > 0 && (
            <div className="flex justify-between text-sm font-semibold text-slate-600">
              <span>Then, recurring</span>
              <span data-testid="quote-doc-recurring-total">{centsToDisplay(t.recurring_total_cents)}{recurringLabel}</span>
            </div>
          )}
        </div>

        {quote.notes && (
          <div className="mt-6">
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Notes</p>
            <p className="text-sm text-slate-600">{quote.notes}</p>
          </div>
        )}
        {quote.terms && (
          <div className="mt-6">
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Terms &amp; Conditions</p>
            <p className="whitespace-pre-line text-sm text-slate-600">{quote.terms}</p>
          </div>
        )}

        {isCustomer && (
          <div className="mt-8 flex flex-wrap gap-3 border-t border-slate-100 pt-6">
            {quote.status === "Accepted" ? (
              <p className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700" data-testid="quote-doc-accepted-note"><CheckCircle2 size={18} /> This quote has been accepted.</p>
            ) : quote.status === "Declined" ? (
              <p className="inline-flex items-center gap-2 text-sm font-bold text-red-600" data-testid="quote-doc-declined-note"><XCircle size={18} /> This quote has been declined.</p>
            ) : quote.status === "Expired" ? (
              <p className="text-sm font-bold text-orange-600" data-testid="quote-doc-expired-note">This quote has expired. Please contact Nova Opsis for an updated quote.</p>
            ) : (
              <>
                <button type="button" onClick={onAccept} disabled={busy || !actionable} data-testid="quote-accept-btn" className="rounded-xl bg-[#0052FF] px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700 disabled:opacity-50">
                  Accept Quote
                </button>
                <button type="button" onClick={onDecline} disabled={busy || !actionable} data-testid="quote-decline-btn" className="rounded-xl border border-slate-300 px-6 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  Decline Quote
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
